import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { BookOpen, ScrollText, GraduationCap, HelpCircle, AlertTriangle, X, Languages, Crown } from "lucide-react";
import { Header } from "@/components/Header";
import { GeneratorForm } from "@/components/GeneratorForm";
import { LectureView } from "@/components/LectureView";
import { StudyGuide } from "@/components/StudyGuide";
import { Quiz } from "@/components/Quiz";
import { BilingualView } from "@/components/BilingualView";
import { Library } from "@/components/Library";
import { SharedLecture } from "@/components/SharedLecture";
import { Dashboard } from "@/components/Dashboard";
import { ExamCountdown } from "@/components/ExamCountdown";
import { QuickStudy } from "@/components/QuickStudy";
import { UpgradeModal } from "@/components/UpgradeModal";
import { ManagePlanModal } from "@/components/ManagePlanModal";
import { OnboardingTour } from "@/components/OnboardingTour";
import { AvatarPicker } from "@/components/AvatarPicker";
import { InstructorPanel } from "@/components/InstructorPanel";
import { StudentClasses } from "@/pages/StudentClasses";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthPage from "@/pages/AuthPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import { AuthCallback } from "@/pages/AuthCallback";
import { JoinPage } from "@/pages/JoinPage";
import { PaymentResult } from "@/pages/PaymentResult";
import { FeaturedGuides } from "@/pages/FeaturedGuides";
import { ForSchools } from "@/pages/ForSchools";
import LandingPage from "@/pages/LandingPage";
import PricingPage from "@/pages/PricingPage";
import AboutPage from "@/pages/AboutPage";
import FeaturesPage from "@/pages/FeaturesPage";
import CustomersPage from "@/pages/CustomersPage";
import VsPage from "@/pages/VsPage";
import TeachersWantedPage from "@/pages/TeachersWantedPage";
import LaunchPage from "@/pages/LaunchPage";
import StudyGroupJoinPage from "@/pages/StudyGroupJoinPage";
import { DeleteAccountDialog } from "@/components/DeleteAccountDialog";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { generateLecture, listLectures, listCommunityLectures, listSavedLectures, toggleLike, toggleSave, deleteLecture, getLecture, getPendingAmbush } from "@/lib/api";
import { LiveGame } from "@/pages/LiveGame";
import GameWatch from "@/pages/GameWatch";
import { LTIDemo } from "@/pages/LTIDemo";
import { LTILaunch } from "@/pages/LTILaunch";
import { PinGate } from "@/components/PinGate";

function MainApp() {
  const [tab, setTab] = useState("create");
  const [dark, setDark] = useState(false);
  const [current, setCurrent] = useState(null);
  const [library, setLibrary] = useState([]);
  const [community, setCommunity] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(false);
  const [budgetLow, setBudgetLow] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [genProgress, setGenProgress] = useState(null);
  const { user, logout } = useAuth();

  useEffect(() => {
    if (user && !localStorage.getItem("lectura-onboarded")) {
      localStorage.setItem("lectura-onboarded", "1");
      setTourOpen(true);
    }
  }, [user]);

  useEffect(() => {
    const h = () => setBudgetLow(true);
    const u = () => setUpgradeOpen(true);
    window.addEventListener("llm-budget-exceeded", h);
    window.addEventListener("needs-upgrade", u);
    return () => {
      window.removeEventListener("llm-budget-exceeded", h);
      window.removeEventListener("needs-upgrade", u);
    };
  }, []);

  // Pop-Quiz Ambush watcher — enrolled students poll every 3s.
  // If a teacher just dropped a pop quiz on any class the user is in, we buzz them and redirect.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    let acknowledgedIds = new Set(JSON.parse(sessionStorage.getItem("lectura_ambush_seen") || "[]"));
    const tick = async () => {
      try {
        const { ambush } = await getPendingAmbush();
        if (!ambush || cancelled) return;
        if (acknowledgedIds.has(ambush.id)) return;
        acknowledgedIds.add(ambush.id);
        sessionStorage.setItem("lectura_ambush_seen", JSON.stringify(Array.from(acknowledgedIds)));
        // Play a buzz sound
        try {
          const Ctor = window.AudioContext || window.webkitAudioContext;
          const ctx = new Ctor();
          [1200, 800, 1400].forEach((f, i) => {
            setTimeout(() => {
              const o = ctx.createOscillator(); const g = ctx.createGain();
              o.type = "square"; o.frequency.value = f; g.gain.value = 0.12;
              o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.12);
            }, i * 150);
          });
        } catch {}
        toast.message("⚡ POP QUIZ from your teacher!", {
          description: `${ambush.lecture_title} — 30 seconds to answer.`,
          duration: 8000,
          action: {
            label: "Play now",
            onClick: () => { window.location.href = `/join-game?code=${ambush.code}`; },
          },
        });
      } catch (e) { /* no-op, endpoint returns null when nothing pending */ }
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  useEffect(() => {
    const saved = localStorage.getItem("lectura-theme");
    const isDark = saved === "dark";
    setDark(isDark);
    document.documentElement.classList.toggle("dark", isDark);
  }, []);

  const toggleTheme = () => {
    setDark((d) => {
      const nd = !d;
      document.documentElement.classList.toggle("dark", nd);
      localStorage.setItem("lectura-theme", nd ? "dark" : "light");
      return nd;
    });
  };

  const refreshLibrary = useCallback(() => {
    listLectures().then(setLibrary).catch(() => {});
    listSavedLectures().then(setSaved).catch(() => {});
  }, []);

  const refreshCommunity = useCallback(() => {
    listCommunityLectures().then(setCommunity).catch(() => {});
  }, []);

  // Detect a bulk-invite hash (/#invite=<token>) and auto-accept once the user is logged in.
  useEffect(() => {
    if (!user) return;
    const m = (window.location.hash || "").match(/invite=([^&]+)/);
    if (!m) return;
    const token = m[1];
    window.history.replaceState(null, "", window.location.pathname);
    import("@/lib/api").then(({ acceptInvite }) =>
      acceptInvite(token)
        .then((r) => toast.success(`You're in ${r.classroom.name}! 🎉`))
        .catch((e) => toast.error(e?.response?.data?.detail || "Invite link is invalid or expired"))
    );
  }, [user]);

  useEffect(() => { refreshLibrary(); }, [refreshLibrary]);


  useEffect(() => {
    if (tab === "explore") refreshCommunity();
  }, [tab, refreshCommunity]);

  const applyLectureUpdate = useCallback((id, patch) => {
    const upd = (arr) => arr.map((l) => (l.id === id ? { ...l, ...patch } : l));
    setLibrary(upd);
    setCommunity(upd);
    setSaved(upd);
  }, []);

  const handleLike = useCallback(async (l) => {
    try {
      const r = await toggleLike(l.id);
      applyLectureUpdate(l.id, { liked: r.liked, like_count: r.like_count });
    } catch (e) { /* ignore */ }
  }, [applyLectureUpdate]);

  const handleSave = useCallback(async (l) => {
    try {
      const r = await toggleSave(l.id);
      applyLectureUpdate(l.id, { saved: r.saved });
      if (r.saved) {
        setSaved((prev) => (prev.some((x) => x.id === l.id) ? prev : [{ ...l, saved: true }, ...prev]));
        toast.success("Saved to your library");
      } else {
        setSaved((prev) => prev.filter((x) => x.id !== l.id));
        toast("Removed from your library");
      }
    } catch (e) { /* ignore */ }
  }, [applyLectureUpdate]);

  const myLibraryList = [
    ...library,
    ...saved.filter((s) => s.saved && !s.owned && !library.some((o) => o.id === s.id)),
  ];

  const handleGenerate = async (payload) => {
    setLoading(true);
    setGenProgress({ phase: "starting", message: "Preparing your study package…" });
    try {
      const lecture = await generateLecture(payload, ({ progress }) => {
        if (progress) setGenProgress(progress);
      });
      setCurrent(lecture);
      setTab("lecture");
      refreshLibrary();
      toast.success("Your lecture is ready!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Generation failed. Please try again.");
    } finally {
      setLoading(false);
      setGenProgress(null);
    }
  };

  const openFromLibrary = (lecture, targetTab) => {
    setCurrent(lecture);
    setTab(targetTab || "lecture");
  };

  const openLectureById = async (id) => {
    try {
      const lec = await getLecture(id);
      setCurrent(lec);
      setTab("lecture");
    } catch {
      toast.error("Could not open that lecture");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteLecture(id);
      setLibrary((lib) => lib.filter((l) => l.id !== id));
      if (current?.id === id) setCurrent(null);
      toast.success("Lecture deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const onScored = (updated) => {
    setCurrent(updated);
    refreshLibrary();
  };

  const EmptyState = ({ icon: Icon, label }) => (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center">
      <Icon className="h-12 w-12 mx-auto text-muted-foreground/40" />
      <p className="mt-4 text-muted-foreground">{label}</p>
      <button
        onClick={() => setTab("create")}
        className="mt-6 rounded-full bg-accent text-accent-foreground px-6 py-2.5 text-sm font-semibold hover:opacity-90 transition"
      >
        Create a Lecture
      </button>
    </div>
  );

  return (
    <div className="App grain min-h-screen">
      <Toaster position="top-center" richColors />
      <Header
        active={tab}
        onNav={setTab}
        dark={dark}
        onToggleTheme={toggleTheme}
        hasLecture={!!current}
        canBilingual={!!current && !!current.language && current.language !== "English"}
        user={user}
        onUpgrade={() => setUpgradeOpen(true)}
        onLogout={logout}
        onQuickStudy={() => setQuickOpen(true)}
        onManagePlan={() => setManageOpen(true)}
        onHelp={() => setTourOpen(true)}
        onAvatar={() => setAvatarOpen(true)}
        onDeleteAccount={() => setDeleteOpen(true)}
      />

      {budgetLow && (
        <div data-testid="budget-banner" className="no-print bg-accent/10 border-b border-accent/30 px-4 py-3 relative z-30">
          <div className="max-w-7xl mx-auto flex items-center gap-3 text-sm">
            <AlertTriangle className="h-4 w-4 text-accent shrink-0" />
            <p className="flex-1 text-foreground/85">
              AI features are paused — your Universal Key balance is used up. Top up at{" "}
              <span className="font-semibold">Profile → Manage plan → Universal Key → Add Balance</span>{" "}
              (enable auto top-up to avoid interruptions).
            </p>
            <button
              data-testid="budget-banner-dismiss"
              onClick={() => setBudgetLow(false)}
              className="grid place-items-center h-7 w-7 rounded-full hover:bg-accent/20 transition"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {(() => {
        const exp = user?.pro_expires_at;
        if (!exp || user?.plan !== "pro") return null;
        const ms = new Date(exp) - new Date();
        const days = Math.ceil(ms / 86400000);
        if (days > 7) return null; // remind within 7 days of expiry
        const expired = ms <= 0;
        const when = new Date(exp).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
        return (
          <div data-testid="renewal-banner" className="no-print bg-primary/10 border-b border-primary/30 px-4 py-3 relative z-30">
            <div className="max-w-7xl mx-auto flex items-center gap-3 text-sm">
              <Crown className="h-4 w-4 text-primary shrink-0" />
              <p className="flex-1 text-foreground/85">
                {expired
                  ? <>Your Lectura Pro pass ended on <span className="font-semibold">{when}</span>. Renew to keep unlimited studying.</>
                  : <>Your Lectura Pro pass ends in <span className="font-semibold">{days} day{days !== 1 && "s"}</span> (on {when}).</>}
              </p>
              <button
                data-testid="renewal-banner-renew"
                onClick={() => setUpgradeOpen(true)}
                className="rounded-full bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:opacity-90 transition"
              >
                Renew now
              </button>
            </div>
          </div>
        );
      })()}


      <main
        className={`relative z-10 ${current && ["lecture", "guide", "quiz"].includes(tab) ? "pb-36 sm:pb-40" : ""}`}
        style={
          current && ["lecture", "guide", "quiz"].includes(tab)
            ? { paddingBottom: "calc(9rem + env(safe-area-inset-bottom, 0px))" }
            : undefined
        }
      >
        {tab === "create" && <GeneratorForm onGenerate={handleGenerate} loading={loading} progress={genProgress} />}

        {tab === "lecture" &&
          (current ? <LectureView lecture={current} /> : <EmptyState icon={BookOpen} label="No active lecture. Create one to begin." />)}

        {tab === "guide" &&
          (current ? <StudyGuide lecture={current} /> : <EmptyState icon={ScrollText} label="No study guide yet." />)}

        {tab === "quiz" &&
          (current ? <Quiz lecture={current} onScored={onScored} /> : <EmptyState icon={HelpCircle} label="No quiz yet." />)}

        {tab === "bilingual" &&
          (current ? <BilingualView lecture={current} /> : <EmptyState icon={Languages} label="No creation open." />)}

        {tab === "dashboard" && (
          <Dashboard onOpenLecture={openLectureById} onUpgrade={() => setUpgradeOpen(true)} onQuickStudy={() => setQuickOpen(true)} />
        )}

        {tab === "exam" && <ExamCountdown />}

        {tab === "instructor" && <InstructorPanel onPreviewLecture={openLectureById} onGoCreate={() => setTab("create")} />}

        {tab === "classes" && (
          <StudentClasses onOpenAssignment={(lectureId) => openLectureById(lectureId)} />
        )}

        {tab === "library" && <Library lectures={myLibraryList} onOpen={openFromLibrary} onDelete={handleDelete} onLike={handleLike} onSave={handleSave} onMoved={refreshLibrary} />}

        {tab === "explore" && (
          <Library
            lectures={community}
            onOpen={openFromLibrary}
            onLike={handleLike}
            onSave={handleSave}
            overline="Explore"
            heading="Community Creations"
            searchPlaceholder="Search all creations by title or book…"
            showAuthor
            enableFilters
          />
        )}
      </main>

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
      <ManagePlanModal open={manageOpen} onClose={() => setManageOpen(false)} onRenew={() => setUpgradeOpen(true)} />
      <OnboardingTour open={tourOpen} onClose={() => { setTourOpen(false); setTab("create"); }} />
      <AvatarPicker open={avatarOpen} onClose={() => setAvatarOpen(false)} />
      <DeleteAccountDialog open={deleteOpen} onOpenChange={setDeleteOpen} onDeleted={() => { logout(); }} />
      <QuickStudy open={quickOpen} onClose={() => setQuickOpen(false)} />

      {/* Sub-nav for active lecture context */}
      {current && ["lecture", "guide", "quiz"].includes(tab) && (
        <div className="no-print fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex gap-1 rounded-full border border-border bg-background/90 backdrop-blur-md p-1 shadow-lg">
          {[
            { id: "lecture", label: "Lecture", icon: BookOpen },
            { id: "guide", label: "Guide", icon: GraduationCap },
            { id: "quiz", label: "Quiz", icon: HelpCircle },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`subnav-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                tab === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Detect Emergent OAuth return synchronously during render (NOT in useEffect) to
  // beat AuthProvider.checkAuth() and prevent an unauthenticated /auth/me racing.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<AuthPage />} />
      <Route path="/register" element={<AuthPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/payment/success" element={<PaymentResult status="success" />} />
      <Route path="/payment/cancel" element={<PaymentResult status="cancel" />} />
      <Route path="/share/:token" element={<SharedLecture />} />
      <Route path="/featured" element={<FeaturedGuides />} />
      <Route path="/for-schools" element={<ForSchools />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/customers" element={<CustomersPage />} />
      <Route path="/vs/:slug" element={<VsPage />} />
      <Route path="/teachers-wanted" element={<TeachersWantedPage />} />
      <Route path="/launch" element={<LaunchPage />} />
      <Route path="/group/:code" element={<StudyGroupJoinPage />} />
      <Route path="/lti/launch" element={<LTILaunch />} />
      <Route path="/lti/demo" element={<ProtectedRoute><LTIDemoPage /></ProtectedRoute>} />
      <Route path="/join/:code" element={<JoinPage />} />
      <Route path="/join-game" element={<ProtectedRoute><LiveGame /></ProtectedRoute>} />
      <Route path="/game/host/:id" element={<ProtectedRoute><LiveGame mode="host" /></ProtectedRoute>} />
      <Route path="/game/watch/:code" element={<ProtectedRoute><GameWatch /></ProtectedRoute>} />
      <Route path="/" element={<HomeRoute />} />
    </Routes>
  );
}

function LTIDemoPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border bg-card/60 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <a href="/" className="inline-flex items-center gap-2 font-serif text-xl font-bold">
            <BookOpen className="h-5 w-5 text-accent" /> Lectura
          </a>
          <a href="/" className="text-sm text-muted-foreground hover:text-foreground transition">← Back to app</a>
        </div>
      </div>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LTIDemo />
      </main>
    </div>
  );
}

// `/` — public marketing landing for anon visitors; authenticated app for logged-in users.
// Follows Hey Catch audit recommendation to stop hiding the product behind an auth wall.
function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <BookOpen className="h-8 w-8 animate-pulse text-accent" />
      </div>
    );
  }
  if (!user) return <LandingPage />;
  return (
    <PinGate>
      <MainApp />
    </PinGate>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
